import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FUENTE, useTema } from './tema';

/**
 * BarraDeMenus — los menús de la pantalla, por categorías.
 *
 * La barra de una reunión había crecido hasta once botones en dos filas, todos
 * gritando igual de fuerte. Buscar «PDF» costaba lo mismo que buscar «Editar la
 * minuta», que es lo que se hace siempre. Un menú resuelve eso sin esconder
 * nada: lo raro deja de ocupar sitio pero sigue estando donde uno iría a
 * buscarlo.
 *
 * ── Dos reglas que no son de estilo ─────────────────────────────────────
 *
 * **Lo que no se puede usar no se apaga: explica.** Un elemento con `traba` se
 * pulsa igual y cuenta qué falta. Deshabilitarlo dejaría a la persona mirando
 * algo gris sin saber por qué, que es la forma más rápida de que abandone.
 *
 * **Lo que se hace siempre no entra aquí.** La acción principal se queda fuera,
 * como botón. Un menú es para lo que se usa de vez en cuando; meter dentro lo
 * cotidiano añade un clic a todo el mundo, todos los días.
 */

const estilos = (C) => ({
  // Ocupa la fila entera a propósito: es lo que permite que los mandos de la
  // derecha lleguen de verdad al borde en vez de quedarse pegados al ultimo
  // menu.
  barra: { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1, minWidth: 0 },

  // El extremo derecho de la barra. Es donde van los mandos que no son menús
  // —cambiar de vista, hoy— porque no despliegan nada y no deben confundirse
  // con los que sí: separados por todo el ancho, nadie los busca desplegables.
  derecha: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 },

  titulo: (abierto) => ({
    padding: '6px 11px', borderRadius: 7, cursor: 'pointer',
    background: abierto ? C.panelAlto : 'transparent',
    border: '1px solid transparent',
    borderColor: abierto ? C.borde : 'transparent',
    color: abierto ? C.texto : C.suave,
    fontSize: 13.5, fontFamily: FUENTE, whiteSpace: 'nowrap'
  }),

  ancla: { position: 'relative' },

  lista: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
    minWidth: 232, padding: 5, borderRadius: 9,
    background: C.panelAlto, border: `1px solid ${C.borde}`,
    boxShadow: '0 10px 30px rgba(0,0,0,.34)'
  },
  // El submenú sale al lado, no debajo: debajo taparía a sus hermanos y no se
  // vería de dónde cuelga.
  //
  // Pegado al borde y no a tres píxeles: ese hueco era suelo por el que se
  // caía el ratón al pasar de «Exportar» a «Word».
  sublista: { top: -5, left: '100%' },

  // El tercer argumento tine su propio color y no una variante del normal: lo
  // que destruye algo tiene que distinguirse ANTES de pulsarlo, y en un menu de
  // una sola columna el unico sitio donde cabe esa senal es el texto.
  elemento: (apagado, resaltado, peligro) => ({
    display: 'flex', alignItems: 'center', gap: 9, width: '100%',
    padding: '7px 10px', borderRadius: 6, cursor: 'pointer', textAlign: 'left',
    background: resaltado ? C.panel : 'transparent', border: 'none',
    color: apagado ? C.tenue : (peligro ? C.error : C.texto),
    fontSize: 13.5, fontFamily: FUENTE, lineHeight: 1.4
  }),
  icono: { width: 16, textAlign: 'center', flexShrink: 0, fontSize: 13 },
  flecha: { marginLeft: 'auto', color: C.tenue, fontSize: 11 },
  marca: { marginLeft: 'auto', color: C.acentoClaro, fontSize: 12 },
  separador: { height: 1, background: C.borde, margin: '5px 8px' },
  encabezado: {
    padding: '7px 10px 4px', fontSize: 11, fontWeight: 600, letterSpacing: .3,
    color: C.tenue, textTransform: 'uppercase'
  }
});

/**
 * @param {Object} props
 * @param {Array} props.menus - [{ nombre, elementos: [...] }]
 * @param {React.ReactNode} props.derecha - mandos sueltos, al otro extremo
 *
 * Cada elemento puede ser:
 *   { separador: true }
 *   { encabezado: 'Texto' }
 *   { id, etiqueta, icono?, detalle?, marcado?, traba?, peligro?, hacer() }
 *   { id, etiqueta, icono?, submenu: [ ...elementos ] }
 */
const BarraDeMenus = ({ menus = [], derecha = null }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const [abierto, setAbierto] = useState(null);
  const [submenu, setSubmenu] = useState(null);
  const caja = useRef(null);

  // ── El submenú no se cierra en cuanto el ratón lo roza ──────────────────
  //
  // «Exportar ▸» cuelga a la derecha y un poco más abajo, así que para llegar
  // a «Word» el ratón cruza por encima de «Publicar en SharePoint». Cerrando al
  // instante, ese roce cerraba el submenú justo cuando se iba hacia él: el
  // camino en diagonal era imposible de recorrer y las opciones de exportar
  // quedaban inalcanzables con el ratón.
  //
  // La solución de toda la vida es esperar un momento antes de cerrar. Si en
  // ese rato el ratón entra en el submenú, se cancela. Un cuarto de segundo no
  // se percibe como retraso y es de sobra para atravesar dos elementos.
  const ESPERA_CIERRE = 260;
  const reloj = useRef(null);

  const pararReloj = useCallback(() => {
    if (reloj.current) { clearTimeout(reloj.current); reloj.current = null; }
  }, []);

  const abrirSubmenu = useCallback((id) => { pararReloj(); setSubmenu(id); }, [pararReloj]);

  const cerrarSubmenuLuego = useCallback(() => {
    pararReloj();
    reloj.current = setTimeout(() => { reloj.current = null; setSubmenu(null); }, ESPERA_CIERRE);
  }, [pararReloj]);

  useEffect(() => pararReloj, [pararReloj]);

  const cerrar = useCallback(() => {
    pararReloj();
    setAbierto(null);
    setSubmenu(null);
  }, [pararReloj]);

  // Pulsar fuera y Escape cierran. Sin lo primero el menú se queda abierto
  // tapando aquello sobre lo que se acaba de pulsar; sin lo segundo no hay
  // forma de salir sin tocar nada, que es lo que espera quien usa el teclado.
  useEffect(() => {
    if (!abierto) return undefined;

    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) cerrar(); };
    const tecla = (e) => { if (e.key === 'Escape') cerrar(); };

    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto, cerrar]);

  const pintarElementos = (elementos, dentro = false) => (
    <div
      style={{ ...S.lista, ...(dentro ? S.sublista : {}) }}
      role="menu"
      // Llegar al submenú cancela el cierre en marcha.
      onMouseEnter={dentro ? pararReloj : undefined}
    >
      {elementos.map((el, i) => {
        if (el.separador) return <div key={`s${i}`} style={S.separador} />;
        if (el.encabezado) return <div key={`e${i}`} style={S.encabezado}>{el.encabezado}</div>;

        if (el.submenu) {
          const dentroAbierto = submenu === el.id;
          return (
            <div
              key={el.id}
              style={S.ancla}
              onMouseEnter={() => abrirSubmenu(el.id)}
              onMouseLeave={cerrarSubmenuLuego}
            >
              <button
                style={S.elemento(false, dentroAbierto)}
                onClick={() => (dentroAbierto ? setSubmenu(null) : abrirSubmenu(el.id))}
                aria-haspopup="true"
                aria-expanded={dentroAbierto}
              >
                <span style={S.icono} aria-hidden="true">{el.icono || ''}</span>
                {el.etiqueta}
                <span style={S.flecha} aria-hidden="true">▸</span>
              </button>
              {dentroAbierto && pintarElementos(el.submenu, true)}
            </div>
          );
        }

        const traba = typeof el.traba === 'function' ? el.traba() : el.traba;
        return (
          <button
            key={el.id}
            style={S.elemento(Boolean(traba), false, Boolean(el.peligro))}
            role="menuitem"
            // Una traba no impide pulsar: al pulsar, cuenta qué falta.
            title={[el.detalle, traba && traba.texto].filter(Boolean).join(' — ')}
            onClick={() => { cerrar(); el.hacer(); }}
            // ── Fuera, el retraso; dentro, la cancelación ────────────────
            //
            // En la lista de arriba, pasar por encima de un hermano no cierra
            // el submenú de golpe: es el camino normal hacia él, y de ahí el
            // retraso.
            //
            // Dentro del submenú es justo al revés, y ponerlo también aquí era
            // el fallo: al posarse sobre «PDF» se arrancaba el temporizador que
            // cerraba el submenú, y 260 ms después se cerraba solo bajo el
            // cursor. El clic caía al vacío. Pasaba con el tercer elemento y
            // casi nunca con el primero, que da tiempo a pulsar antes.
            //
            // Estar dentro del submenú es la prueba de que se va a él: cancela.
            onMouseEnter={dentro ? pararReloj : cerrarSubmenuLuego}
          >
            <span style={S.icono} aria-hidden="true">{el.icono || ''}</span>
            {el.etiqueta}
            {el.marcado && <span style={S.marca} aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </div>
  );

  if (!menus.length && !derecha) return null;

  return (
    <div style={S.barra} ref={caja}>
      {menus.map(menu => (
        <div key={menu.nombre} style={S.ancla}>
          <button
            style={S.titulo(abierto === menu.nombre)}
            onClick={() => {
              setSubmenu(null);
              setAbierto(a => (a === menu.nombre ? null : menu.nombre));
            }}
            // Con un menú ya abierto, pasar por encima de otro lo cambia. Es lo
            // que hace cualquier barra de menús y lo que la mano espera.
            onMouseEnter={() => { if (abierto) { setAbierto(menu.nombre); setSubmenu(null); } }}
            aria-haspopup="true"
            aria-expanded={abierto === menu.nombre}
          >
            {menu.nombre}
          </button>
          {abierto === menu.nombre && pintarElementos(menu.elementos)}
        </div>
      ))}

      {derecha && <div style={S.derecha}>{derecha}</div>}
    </div>
  );
};

export default BarraDeMenus;
